import { useState, useEffect, useRef, useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { XCircle, HelpCircle, Check, X, Bookmark } from 'lucide-react';
import { Icon } from '@/components/atoms/Icon';
import { useSavedVocabulary } from '@/context/SavedVocabularyContext';
import type {
  Activity,
  UserAnswer,
  MultipleChoiceAnswer,
  TrueFalseAnswer,
  FillInBlankTapAnswer,
  MatchingAnswer,
  OrderingAnswer,
  VocabularyCardAnswer,
  AudioTranslationSelectAnswer,
  AudioOrderingAnswer,
  AudioDictationAnswer,
  TranslationTapAnswer,
  TranslationSelectAnswer,
  SentenceCompleteTapAnswer,
  FillInBlankTapActivity,
  OrderingActivity,
} from '@/types/story.types';
import type { MistakeReviewSessionPayload } from '@/types/mistakeReview.types';
import { MistakesReviewDrawer } from './MistakesReviewDrawer';
import { WrongExplanationDrawer } from '@/components/molecules/WrongExplanationDrawer';
import { useStoryBottomBar } from '@/context/StoryBottomBarContext';
import { useToast } from '@/context/ToastContext';
import { useProfileNavGuard } from '@/context/ProfileNavGuardContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/molecules/Dialog';
import { getRandomStudentCongratsKey } from '@/constants/congratsMessages';
import { shuffle } from '@/utils/shuffle';
import { MatchingCards } from './MatchingCards';
import { FillInBlankTap } from './FillInBlankTap';
import { OrderingTap } from './OrderingTap';
import { AudioActivityPlayer } from './AudioActivityPlayer';
import { usersAPI } from '@/services/api/users.api';
import styles from '../StoryPage.module.scss';

interface FeedbackState {
  isCorrect: boolean;
  correctAnswerText: string;
  explanation?: string;
  /** Per-position correctness for tap-based types (ordering, audio_ordering,
   * fill_in_blank_tap, translation_tap). Drives chip coloring and powers the
   * "intentar de nuevo" gating. */
  correctness?: boolean[];
  /** Set by applyFeedback when the user fell short on a tap-based activity but
   * came close enough (≥ 70 % de chips correctos) to deserve a second chance. */
  allowRetry?: boolean;
}

/** Activities that may show an in-place retry after a failed first attempt. */
const TAP_RETRY_TYPES: ReadonlySet<Activity['type']> = new Set([
  'ordering',
  'audio_ordering',
  'fill_in_blank_tap',
  'translation_tap',
]);

const RETRY_THRESHOLD = 0.7;

/** Misma señal que Ajustes → «Interfaz»: si la UI está en inglés mostramos
 * `explanation`; si está en español usamos `explanation_translation` y caemos
 * al inglés si la traducción aún no existe (historias legacy). El texto se
 * muestra on-demand dentro del drawer «Por qué no es correcta». */
function pickExplanation(
  activity: Pick<Activity, 'explanation' | 'explanation_translation'>,
  interfaceEnglish: boolean
): string | undefined {
  if (interfaceEnglish) return activity.explanation;
  const es = activity.explanation_translation?.trim();
  return es && es.length > 0 ? es : activity.explanation;
}

function checkAnswer(
  activity: Activity,
  userAnswer: UserAnswer,
  t: TFunction
): Omit<FeedbackState, 'explanation' | 'allowRetry'> {
  switch (activity.type) {
    case 'multiple_choice': {
      const act = activity;
      const ans = userAnswer as MultipleChoiceAnswer;
      return {
        isCorrect: ans.selectedIndex === act.correctAnswerIndex,
        correctAnswerText: act.options[act.correctAnswerIndex] ?? '',
      };
    }
    case 'true_false': {
      const act = activity;
      const ans = userAnswer as TrueFalseAnswer;
      return {
        isCorrect: ans.answer === act.correctAnswer,
        correctAnswerText: act.correctAnswer ? t('activities.true') : t('activities.false'),
      };
    }
    case 'fill_in_blank_tap': {
      const act = activity;
      const ans = userAnswer as FillInBlankTapAnswer;
      const correctness = act.correctAnswers.map(
        (correct, i) =>
          (ans.answers[i] ?? '').trim().toLowerCase() === correct.trim().toLowerCase()
      );
      return {
        isCorrect: correctness.every(Boolean),
        correctAnswerText: act.correctAnswers.join(', '),
        correctness,
      };
    }
    case 'matching': {
      const act = activity;
      const ans = userAnswer as MatchingAnswer;
      const isCorrect = act.pairs.every((correctPair) =>
        ans.pairs.some(
          (userPair) => userPair.left === correctPair.left && userPair.right === correctPair.right
        )
      );
      return {
        isCorrect,
        correctAnswerText: act.pairs.map((p) => `${p.left} → ${p.right}`).join(', '),
      };
    }
    case 'ordering': {
      const act = activity;
      const ans = userAnswer as OrderingAnswer;
      const correctness = ans.order.map((itemIdx, i) => {
        const correctIdx = act.correctOrder[i];
        if (correctIdx === undefined) return false;
        return (
          (act.items[itemIdx] ?? '').trim().toLowerCase() ===
          (act.items[correctIdx] ?? '').trim().toLowerCase()
        );
      });
      const isCorrect =
        ans.order.length === act.correctOrder.length && correctness.every(Boolean);
      return {
        isCorrect,
        correctAnswerText: act.correctOrder.map((i) => act.items[i]).join(' → '),
        correctness,
      };
    }
    case 'vocabulary_card':
      return { isCorrect: true, correctAnswerText: '' };
    case 'audio_translation_select': {
      const act = activity;
      const ans = userAnswer as AudioTranslationSelectAnswer;
      return {
        isCorrect: ans.selectedIndex === act.correctAnswerIndex,
        correctAnswerText: act.options[act.correctAnswerIndex] ?? '',
      };
    }
    case 'audio_ordering': {
      const act = activity;
      const ans = userAnswer as AudioOrderingAnswer;
      const correctness = ans.order.map((itemIdx, i) => {
        const correctIdx = act.correctOrder[i];
        if (correctIdx === undefined) return false;
        return (
          (act.words[itemIdx] ?? '').trim().toLowerCase() ===
          (act.words[correctIdx] ?? '').trim().toLowerCase()
        );
      });
      const isCorrect =
        ans.order.length === act.correctOrder.length && correctness.every(Boolean);
      return {
        isCorrect,
        correctAnswerText: act.correctOrder.map((i) => act.words[i]).join(' → '),
        correctness,
      };
    }
    case 'audio_dictation': {
      const act = activity;
      const ans = userAnswer as AudioDictationAnswer;
      return {
        isCorrect: ans.answer.trim().toLowerCase() === act.correctAnswer.trim().toLowerCase(),
        correctAnswerText: act.correctAnswer,
      };
    }
    case 'translation_tap': {
      const act = activity;
      const ans = userAnswer as TranslationTapAnswer;
      const correctness = act.correctAnswers.map(
        (correct, i) =>
          (ans.answers[i] ?? '').trim().toLowerCase() === correct.trim().toLowerCase()
      );
      return {
        isCorrect: correctness.every(Boolean),
        correctAnswerText: act.correctAnswers.join(' '),
        correctness,
      };
    }
    case 'translation_select': {
      const act = activity;
      const ans = userAnswer as TranslationSelectAnswer;
      return {
        isCorrect: ans.selectedIndex === act.correctAnswerIndex,
        correctAnswerText: act.options[act.correctAnswerIndex] ?? '',
      };
    }
    case 'sentence_complete_tap': {
      const act = activity;
      const ans = userAnswer as SentenceCompleteTapAnswer;
      return {
        isCorrect: ans.answer.trim().toLowerCase() === act.correctAnswer.trim().toLowerCase(),
        correctAnswerText: act.correctAnswer,
      };
    }
    default:
      return { isCorrect: false, correctAnswerText: '' };
  }
}

interface ActivitiesTabProps {
  activities: Activity[];
  onSubmit: (answers: UserAnswer[]) => void;
  isSubmitting?: boolean;
  sourceType: 'story' | 'resource' | 'conversation';
  sourceId: string;
  /** Desde la ruta (resultados o fin de reto): misma hidratación que tras navigate desde el drawer */
  mistakeReviewSession?: MistakeReviewSessionPayload | null;
  /** Sustituye estado local por navigate(replace) para un solo camino de entrada al repaso */
  onRequestMistakeReview: (session: MistakeReviewSessionPayload) => void;
}

/**
 * ActivitiesTab Component (A-04)
 * Displays activities one at a time (carousel) and allows users to submit answers.
 * Activity types: multiple_choice, fill_in_blank_tap, true_false, matching, ordering,
 *                 vocabulary_card, audio_translation_select, audio_ordering, audio_dictation,
 *                 translation_tap, translation_select, sentence_complete_tap
 */
const SUBMIT_DELAY_MS = 600;
const PROGRESS_BAR_TRANSITION_MS = 450;
const MATCHING_FEEDBACK_DELAY_MS = 500;

function buildPreservedAnswersOutsideQueue(
  allAnswers: Record<string, UserAnswer>,
  queueIds: string[],
  list: Activity[]
): Record<string, UserAnswer> {
  const inQueue = new Set(queueIds);
  const out: Record<string, UserAnswer> = {};
  for (const a of list) {
    if (inQueue.has(a.id)) continue;
    const ans = allAnswers[a.id];
    if (ans) out[a.id] = ans;
  }
  return out;
}

/** Solo actividades visibles en el cliente; evita enviar huérfanas que el backend califica como incorrectas. */
function buildSubmitAnswersForGrading(
  answers: Record<string, UserAnswer>,
  list: Activity[]
): UserAnswer[] {
  const allowed = new Set(list.map((a) => a.id));
  const candidates = Object.values(answers).filter(
    (a) => a.activityId && allowed.has(a.activityId)
  );
  const byId = new Map<string, UserAnswer>();
  for (const a of candidates) {
    byId.set(a.activityId, a);
  }
  return [...byId.values()];
}

/** Types that are graded by the backend (can fail). */
const GRADABLE_TYPES = new Set([
  'multiple_choice',
  'fill_in_blank_tap',
  'true_false',
  'matching',
  'ordering',
  'audio_translation_select',
  'audio_ordering',
  'audio_dictation',
  'translation_tap',
  'translation_select',
  'sentence_complete_tap',
]);

/**
 * Compute the initial review queue: intersection of requested IDs with gradable
 * activities that actually exist in the list, preserving story order.
 */
function computeInitialReviewQueue(
  session: MistakeReviewSessionPayload | null | undefined,
  activityList: Activity[]
): string[] | null {
  if (!session?.queueIds.length || !activityList.length) return null;
  const requested = new Set(session.queueIds);
  return activityList
    .filter((a) => requested.has(a.id) && GRADABLE_TYPES.has(a.type))
    .map((a) => a.id);
}

/**
 * Build the initial answers map for a review session:
 * only preserved answers for activities NOT in the queue.
 */
function computeInitialAnswersForReview(
  session: MistakeReviewSessionPayload | null | undefined,
  queueIds: string[] | null,
  activityList: Activity[]
): Record<string, UserAnswer> {
  if (!session || !queueIds) return {};
  const inQueue = new Set(queueIds);
  const allowed = new Set(activityList.map((a) => a.id));
  const out: Record<string, UserAnswer> = {};
  for (const [id, ans] of Object.entries(session.preservedAnswers ?? {})) {
    if (!inQueue.has(id) && allowed.has(id)) out[id] = ans;
  }
  return out;
}

/** Mezcla `options` y reajusta `correctAnswerIndex` para evitar que el LLM
 * deje siempre la respuesta correcta en index 0. Se aplica una vez por carga
 * de actividades, antes del primer render. */
const SHUFFLEABLE_TYPES = new Set([
  'multiple_choice',
  'translation_select',
  'audio_translation_select',
]);

function shuffleActivityOptions(activity: Activity): Activity {
  if (!SHUFFLEABLE_TYPES.has(activity.type)) return activity;
  const act = activity as Activity & { options?: string[]; correctAnswerIndex?: number };
  const options = act.options;
  const correctIdx = act.correctAnswerIndex;
  if (!Array.isArray(options) || options.length < 2) return activity;
  if (typeof correctIdx !== 'number' || correctIdx < 0 || correctIdx >= options.length) {
    return activity;
  }
  const correctValue = options[correctIdx];
  const shuffled = shuffle(options);
  const newIdx = shuffled.indexOf(correctValue!);
  if (newIdx < 0) return activity;
  return { ...act, options: shuffled, correctAnswerIndex: newIdx } as Activity;
}

export function ActivitiesTab({
  activities,
  onSubmit,
  isSubmitting = false,
  sourceType,
  sourceId,
  mistakeReviewSession = null,
  onRequestMistakeReview,
}: ActivitiesTabProps) {
  const activityList = useMemo(() => activities.map(shuffleActivityOptions), [activities]);
  const activityListRef = useRef(activityList);
  activityListRef.current = activityList;

  // --- Initial state computed synchronously from props (no effect race) ---
  const initialQueueIds = useMemo(
    () => computeInitialReviewQueue(mistakeReviewSession, activityList),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // only on mount — remount via key={location.key} handles new sessions
  );
  const initialAnswers = useMemo(
    () => computeInitialAnswersForReview(mistakeReviewSession, initialQueueIds, activityList),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const initialFailedIds = useMemo(
    () => (initialQueueIds ? [...initialQueueIds] : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [answers, setAnswers] = useState<Record<string, UserAnswer>>(initialAnswers);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const [showValidation, setShowValidation] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isContentHidden, setIsContentHidden] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);
  const [whyWrongOpen, setWhyWrongOpen] = useState(false);
  // Cierra el drawer de explicación si el feedback desaparece (continue, retry,
  // navegación entre actividades). Evita que quede abierto al pasar a la
  // siguiente pregunta.
  useEffect(() => {
    if (!feedback) setWhyWrongOpen(false);
  }, [feedback]);
  const [wordAudioPlaying, setWordAudioPlaying] = useState(false);
  const wordAudioRef = useRef<HTMLAudioElement | null>(null);
  const [failedActivityIds, setFailedActivityIds] = useState<string[]>(initialFailedIds);
  const failedActivityIdsRef = useRef<string[]>([]);
  failedActivityIdsRef.current = failedActivityIds;
  const handleSubmitRef = useRef<() => void>(() => {});
  const [showMistakesReviewPrompt, setShowMistakesReviewPrompt] = useState(false);
  const [mistakeReviewQueueIds, setMistakeReviewQueueIds] = useState<string[] | null>(initialQueueIds);
  /** Actividades que ya consumieron su único reintento en esta sesión. */
  const [retryUsed, setRetryUsed] = useState<Record<string, boolean>>({});
  /** Contador por actividad para forzar remount del componente tap en el reintento. */
  const [tapRetryKey, setTapRetryKey] = useState<Record<string, number>>({});
  const { setContent: setBottomBarContent, setHideCloseButton, setHeaderProgress } = useStoryBottomBar() ?? {};

  useEffect(() => {
    setHideCloseButton?.(false);
  }, [setHideCloseButton]);

  useEffect(() => () => setHeaderProgress?.(null), [setHeaderProgress]);

  const displayActivityList = useMemo(() => {
    const sorted = [...activityList].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    if (mistakeReviewQueueIds === null) return sorted;
    const idSet = new Set(mistakeReviewQueueIds);
    return sorted.filter((a) => idSet.has(a.id));
  }, [activityList, mistakeReviewQueueIds]);

  const currentActivityIdRef = useRef<string | null>(null);
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const interfaceEnglish = i18n.language.startsWith('en');
  const { saveItem: saveVocabItem, removeItem: removeVocabItem, isSaved: isVocabSaved } = useSavedVocabulary();


  const applyFeedback = (
    result: Omit<FeedbackState, 'explanation' | 'allowRetry'>,
    activity: Activity,
    userAnswer: UserAnswer
  ) => {
    const wasRetry = retryUsed[activity.id] === true;
    const isTapRetryType = TAP_RETRY_TYPES.has(activity.type);
    const total = result.correctness?.length ?? 0;
    const correctCount = result.correctness?.filter(Boolean).length ?? 0;
    // Redondeo hacia abajo para que un swap único (que siempre arrastra ≥ 2 fallos
     // en ordering/audio_ordering) no penalice retries en frases cortas.
    const meetsThreshold = total > 0 && correctCount >= Math.floor(RETRY_THRESHOLD * total);
    const allowRetry =
      isTapRetryType && !result.isCorrect && !wasRetry && meetsThreshold;

    setFeedback({ ...result, allowRetry, explanation: pickExplanation(activity, interfaceEnglish) });
    setFailedActivityIds((prev) => {
      if (!result.isCorrect) {
        return prev.includes(activity.id) ? prev : [...prev, activity.id];
      }
      // Primera pasada + retry-correct: mantenemos la actividad como fallada para
      // que el prompt de repaso final la incluya. En modo "mistake review" sí la
      // sacamos cuando se acierta, igual que antes.
      if (mistakeReviewQueueIds === null && wasRetry) {
        return prev;
      }
      return prev.filter((id) => id !== activity.id);
    });
    if (!result.isCorrect && !wasRetry) {
      usersAPI.saveActivityError({
        activityId: activity.id,
        activityType: activity.type,
        sourceType,
        sourceId,
        userAnswer,
      }).catch(() => {}); // silent — no afecta la UI
    }
  };

  const handleRetry = () => {
    if (!currentActivity || !feedback?.allowRetry) return;
    const id = currentActivity.id;
    const correctness = feedback.correctness ?? [];
    setRetryUsed((prev) => ({ ...prev, [id]: true }));
    setShowValidation(false);
    setFeedback(null);
    setAnswers((prev) => {
      const ans = prev[id];
      if (!ans) return prev;
      switch (ans.type) {
        case 'ordering':
          return {
            ...prev,
            [id]: { ...(ans), order: [] } as OrderingAnswer,
          };
        case 'audio_ordering':
          return {
            ...prev,
            [id]: { ...(ans), order: [] } as AudioOrderingAnswer,
          };
        case 'fill_in_blank_tap': {
          const a = ans;
          return {
            ...prev,
            [id]: { ...a, answers: a.answers.map((w, i) => (correctness[i] ? w : '')) },
          };
        }
        case 'translation_tap': {
          const a = ans;
          return {
            ...prev,
            [id]: { ...a, answers: a.answers.map((w, i) => (correctness[i] ? w : '')) },
          };
        }
        default:
          return prev;
      }
    });
    setTapRetryKey((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));
  };
  const [, setConsecutiveCorrect] = useState(0);

  // Show congratulatory toast when 3 correct in a row (on feedback, not on Continue)
  useEffect(() => {
    if (!feedback) return;
    if (feedback.isCorrect) {
      setConsecutiveCorrect((prev) => {
        const next = prev + 1;
        if (next >= 3) {
          toast({
            description: t(getRandomStudentCongratsKey()),
            variant: 'motivation',
            duration: 4000,
          });
          return 0;
        }
        return next;
      });
    } else {
      setConsecutiveCorrect(0);
    }
  }, [feedback, toast, t]);

  // Guard de salida: mientras las actividades estén en curso, cerrar la historia
  // (X del header / navegación del menú) pide confirmación porque el progreso no
  // se guarda hasta el envío final. Mismo puente que Perfil/Ajustes; el envío
  // navega programáticamente al resultado, así que nunca dispara esta modal.
  const navGuard = useProfileNavGuard();
  const [leaveOpen, setLeaveOpen] = useState(false);
  const proceedLeaveRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    navGuard.setGuard({
      isDirty: () => true,
      confirmLeave: (proceed) => {
        proceedLeaveRef.current = proceed;
        setLeaveOpen(true);
      },
    });
    return () => navGuard.setGuard(null);
  }, [navGuard]);

  const handleLeaveStay = () => {
    setLeaveOpen(false);
    proceedLeaveRef.current = null;
  };

  const handleLeaveConfirm = () => {
    setLeaveOpen(false);
    const proceed = proceedLeaveRef.current;
    proceedLeaveRef.current = null;
    proceed?.();
  };

  // Auto-initialize answers for activities that need default state
  useEffect(() => {
    const activity = displayActivityList[currentIndex];
    if (!activity || answers[activity.id]) return;

    if (activity.type === 'ordering') {
      // Tap-based: start with empty sequence (pool managed internally by OrderingTap)
      setAnswers((prev) => ({
        ...prev,
        [activity.id]: {
          activityId: activity.id,
          type: 'ordering',
          order: [],
          timeSpent: 0,
        } as OrderingAnswer,
      }));
    } else if (activity.type === 'audio_ordering') {
      setAnswers((prev) => ({
        ...prev,
        [activity.id]: {
          activityId: activity.id,
          type: 'audio_ordering',
          order: [],
          timeSpent: 0,
        } as AudioOrderingAnswer,
      }));
    } else if (activity.type === 'audio_dictation') {
      setAnswers((prev) => ({
        ...prev,
        [activity.id]: {
          activityId: activity.id,
          type: 'audio_dictation',
          answer: '',
          timeSpent: 0,
        } as AudioDictationAnswer,
      }));
    } else if (activity.type === 'translation_tap') {
      setAnswers((prev) => ({
        ...prev,
        [activity.id]: {
          activityId: activity.id,
          type: 'translation_tap',
          answers: [],
          timeSpent: 0,
        } as TranslationTapAnswer,
      }));
    } else if (activity.type === 'sentence_complete_tap') {
      setAnswers((prev) => ({
        ...prev,
        [activity.id]: {
          activityId: activity.id,
          type: 'sentence_complete_tap',
          answer: '',
          timeSpent: 0,
        } as SentenceCompleteTapAnswer,
      }));
    } else if (activity.type === 'matching') {
      const act = activity;
      setAnswers((prev) => ({
        ...prev,
        [activity.id]: {
          activityId: activity.id,
          type: 'matching',
          pairs: act.pairs.map((p) => ({ left: p.left, right: '' })),
          timeSpent: 0,
        } as MatchingAnswer,
      }));
    } else if (activity.type === 'vocabulary_card') {
      setAnswers((prev) => ({
        ...prev,
        [activity.id]: {
          activityId: activity.id,
          type: 'vocabulary_card',
          timeSpent: 0,
        } as VocabularyCardAnswer,
      }));
      // Stop word audio when moving to a new activity
      if (wordAudioRef.current) {
        wordAudioRef.current.pause();
        wordAudioRef.current = null;
      }
      setWordAudioPlaying(false);
    }
  {/* eslint-disable-next-line react-hooks/exhaustive-deps */}
  }, [currentIndex, displayActivityList]);

  const currentActivity = displayActivityList[currentIndex];
  currentActivityIdRef.current = currentActivity?.id ?? null;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleMultipleChoiceChange = (activityId: string, value: string) => {
    setShowValidation(false);
    const selectedIndex = parseInt(value, 10);
    const activity = activityList.find((a) => a.id === activityId);
    /** `selectedOption` viaja con la respuesta para que el backend pueda
     * calificar por valor — el `selectedIndex` se refiere al orden barajado
     * del cliente y no coincide con el `correctAnswerIndex` original en BD. */
    const selectedOption =
      activity?.type === 'multiple_choice' ? activity.options[selectedIndex] ?? '' : '';
    const newAnswer: MultipleChoiceAnswer = {
      activityId,
      type: 'multiple_choice',
      selectedIndex,
      selectedOption,
      timeSpent: 0,
    };
    setAnswers((prev) => ({ ...prev, [activityId]: newAnswer }));
    if (activity?.type === 'multiple_choice') {
      const result = checkAnswer(activity, newAnswer, t);
      applyFeedback(result, activity, newAnswer);
    }
  };

  const handleTrueFalseChange = (activityId: string, value: string) => {
    setShowValidation(false);
    const newAnswer: TrueFalseAnswer = {
      activityId,
      type: 'true_false',
      answer: value === 'true',
      timeSpent: 0,
    };
    setAnswers((prev) => ({ ...prev, [activityId]: newAnswer }));
    const activity = activityList.find((a) => a.id === activityId);
    if (activity?.type === 'true_false') {
      const result = checkAnswer(activity, newAnswer, t);
      applyFeedback(result, activity, newAnswer);
    }
  };

  const handleMatchingChange = (activityId: string, leftItem: string, rightItem: string) => {
    setShowValidation(false);
    setAnswers((prev) => {
      const existing = prev[activityId] as MatchingAnswer | undefined;
      const newPairs = (existing?.pairs ?? []).map((p) =>
        p.left === leftItem ? { left: p.left, right: rightItem } : p
      );
      const newAnswer: MatchingAnswer = {
        activityId,
        type: 'matching',
        pairs: newPairs,
        timeSpent: 0,
      };
      const allMatched = newPairs.every((p) => p.right !== '');
      if (allMatched) {
        const activity = activityList.find((a) => a.id === activityId);
        if (activity?.type === 'matching') {
          setTimeout(() => {
            if (currentActivityIdRef.current === activityId) {
              const result = checkAnswer(activity, newAnswer, t);
              applyFeedback(result, activity, newAnswer);
            }
          }, MATCHING_FEEDBACK_DELAY_MS);
        }
      }
      return { ...prev, [activityId]: newAnswer };
    });
  };

  const handleOrderingChange = (activityId: string, order: number[]) => {
    setAnswers((prev) => ({
      ...prev,
      [activityId]: { activityId, type: 'ordering', order, timeSpent: 0 } as OrderingAnswer,
    }));
  };

  const handleAudioTranslationSelectChange = (activityId: string, value: string) => {
    setShowValidation(false);
    const selectedIndex = parseInt(value, 10);
    const activity = activityList.find((a) => a.id === activityId);
    const selectedOption =
      activity?.type === 'audio_translation_select'
        ? activity.options[selectedIndex] ?? ''
        : '';
    const newAnswer: AudioTranslationSelectAnswer = {
      activityId,
      type: 'audio_translation_select',
      selectedIndex,
      selectedOption,
      timeSpent: 0,
    };
    setAnswers((prev) => ({ ...prev, [activityId]: newAnswer }));
    if (activity?.type === 'audio_translation_select') {
      const result = checkAnswer(activity, newAnswer, t);
      applyFeedback(result, activity, newAnswer);
    }
  };

  const handleAudioOrderingChange = (activityId: string, order: number[]) => {
    setAnswers((prev) => ({
      ...prev,
      [activityId]: { activityId, type: 'audio_ordering', order, timeSpent: 0 } as AudioOrderingAnswer,
    }));
  };

  const handleAudioDictationChange = (activityId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [activityId]: { activityId, type: 'audio_dictation', answer: value, timeSpent: 0 } as AudioDictationAnswer,
    }));
  };

  const handleTranslationTapChange = (activityId: string, answers: string[]) => {
    setAnswers((prev) => ({
      ...prev,
      [activityId]: { activityId, type: 'translation_tap', answers, timeSpent: 0 } as TranslationTapAnswer,
    }));
  };

  const handleTranslationSelectChange = (activityId: string, value: string) => {
    setShowValidation(false);
    const selectedIndex = parseInt(value, 10);
    const activity = activityList.find((a) => a.id === activityId);
    const selectedOption =
      activity?.type === 'translation_select' ? activity.options[selectedIndex] ?? '' : '';
    const newAnswer: TranslationSelectAnswer = {
      activityId,
      type: 'translation_select',
      selectedIndex,
      selectedOption,
      timeSpent: 0,
    };
    setAnswers((prev) => ({ ...prev, [activityId]: newAnswer }));
    if (activity?.type === 'translation_select') {
      const result = checkAnswer(activity, newAnswer, t);
      applyFeedback(result, activity, newAnswer);
    }
  };

  const handleSentenceCompleteTapChange = (activityId: string, answer: string) => {
    setAnswers((prev) => ({
      ...prev,
      [activityId]: { activityId, type: 'sentence_complete_tap', answer, timeSpent: 0 } as SentenceCompleteTapAnswer,
    }));
  };

  // ── Submit & review flow ─────────────────────────────────────────────────

  const handleSubmit = () => {
    const currentAnswers = answersRef.current;
    // Solo comprobamos actividades gradables: vocabulary_card es informacional y
    // puede no tener respuesta local en sesiones de repaso sin afectar al envío.
    const allAnswered = activityList
      .filter((a) => GRADABLE_TYPES.has(a.type))
      .every((activity) => currentAnswers[activity.id]);
    if (!allAnswered) {
      setShowValidation(true);
      return;
    }
    setIsCompleting(true);
    const answersArray = buildSubmitAnswersForGrading(currentAnswers, activityList);
    setTimeout(() => {
      setIsContentHidden(true);
      setHideCloseButton?.(true);
    }, PROGRESS_BAR_TRANSITION_MS);
    setTimeout(() => {
      setMistakeReviewQueueIds(null);
      onSubmit(answersArray);
    }, SUBMIT_DELAY_MS);
  };

  handleSubmitRef.current = handleSubmit;

  /** Duolingo-style: after the last activity, offer to redo mistakes before API submit */
  const tryFinishOrPromptReview = () => {
    setShowValidation(false);
    setFeedback(null);
    const list = activityListRef.current;
    const stillFailed = failedActivityIdsRef.current.filter(
      (id) => list.some((a) => a.id === id && GRADABLE_TYPES.has(a.type))
    );
    if (stillFailed.length > 0) {
      setShowMistakesReviewPrompt(true);
      return;
    }
    handleSubmit();
  };

  const handleReviewMistakesFromPrompt = () => {
    const list = activityListRef.current;
    const ids = failedActivityIdsRef.current.filter(
      (id) => list.some((a) => a.id === id && GRADABLE_TYPES.has(a.type))
    );
    const orderedQueue = list.filter((a) => ids.includes(a.id)).map((a) => a.id);
    if (orderedQueue.length === 0) {
      setShowMistakesReviewPrompt(false);
      handleSubmitRef.current();
      return;
    }
    const preservedAnswers = buildPreservedAnswersOutsideQueue(answersRef.current, orderedQueue, list);
    onRequestMistakeReview({ queueIds: orderedQueue, preservedAnswers });
  };

  const handleSubmitAnywayFromPrompt = () => {
    setShowMistakesReviewPrompt(false);
    handleSubmitRef.current();
  };

  if (activityList.length === 0) {
    return (
      <div className={styles.story__empty}>
        <Icon icon={HelpCircle} size="lg" />
        <p className={styles.story__emptyText}>{t('activities.noActivities')}</p>
        <p className={styles.story__emptySubtext}>{t('activities.noActivitiesSub')}</p>
      </div>
    );
  }

  const isLastActivity = currentIndex === displayActivityList.length - 1;

  const isCurrentAnswered = (() => {
    if (!currentActivity) return false;
    const answer = answers[currentActivity.id];
    if (!answer) return false;
    if (currentActivity.type === 'ordering') return true;
    if (currentActivity.type === 'matching') {
      const ans = answer as MatchingAnswer;
      return ans.pairs.every((p) => p.right !== '');
    }
    if (currentActivity.type === 'fill_in_blank_tap') {
      const ans = answer as FillInBlankTapAnswer;
      const blankCount = (currentActivity).correctAnswers.length;
      return ans.answers?.length >= blankCount && ans.answers.every((a) => a !== '');
    }
    if (currentActivity.type === 'audio_dictation') {
      const ans = answer as AudioDictationAnswer;
      return ans.answer.trim().length > 0;
    }
    return true;
  })();

  const progressValue = isCompleting
    ? displayActivityList.length
    : currentIndex === 0
      ? 0.25
      : currentIndex;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (isContentHidden || displayActivityList.length === 0) {
      setHeaderProgress?.(null);
      return;
    }
    setHeaderProgress?.({
      value: Math.min(progressValue, displayActivityList.length),
      max: displayActivityList.length,
    });
  }, [progressValue, displayActivityList.length, isContentHidden, setHeaderProgress]);

  // Ref always points to the latest handleCheck, preventing stale closures
  // in the bottom-bar useEffect.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleCheckRef = useRef<() => void>(() => {});

  const handleCheck = () => {
    if (!currentActivity || !isCurrentAnswered) return;
    // Vocabulary card: skip feedback, go straight to next
    if (currentActivity.type === 'vocabulary_card') {
      handleContinue();
      return;
    }
    const userAnswer = answers[currentActivity.id];
    if (!userAnswer) return;
    const result = checkAnswer(currentActivity, userAnswer, t);
    applyFeedback(result, currentActivity, userAnswer);
  };

  handleCheckRef.current = handleCheck;

  const handleContinue = () => {
    if (isLastActivity) {
      tryFinishOrPromptReview();
    } else {
      setFeedback(null);
      setCurrentIndex((i) => i + 1);
      setShowValidation(false);
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const checkButtonLabel = useMemo(() => {
    if (isSubmitting || isCompleting) return t('activities.submitting');
    if (currentActivity?.type === 'vocabulary_card') {
      return isLastActivity ? t('activities.finish') : t('activities.continue');
    }
    return t('activities.check');
  }, [isSubmitting, isCompleting, currentActivity?.type, isLastActivity, t]);

  // Hide the Check button for activities that auto-trigger feedback (no manual confirm needed)
  const hideCheckButton =
    currentActivity?.type === 'multiple_choice' ||
    currentActivity?.type === 'true_false' ||
    currentActivity?.type === 'matching' ||
    currentActivity?.type === 'fill_in_blank_tap' ||
    currentActivity?.type === 'ordering' ||
    currentActivity?.type === 'audio_translation_select' ||
    currentActivity?.type === 'audio_ordering' ||
    currentActivity?.type === 'translation_tap' ||
    currentActivity?.type === 'translation_select' ||
    currentActivity?.type === 'sentence_complete_tap';

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (isContentHidden || showMistakesReviewPrompt || feedback || hideCheckButton) {
      setBottomBarContent?.(null);
      return () => setBottomBarContent?.(null);
    }
    setBottomBarContent?.(
      <Button
        variant="primary"
        size="lg"
        onClick={() => handleCheckRef.current()}
        disabled={isSubmitting || isCompleting || !isCurrentAnswered}
        className={styles.story__portalButton}
      >
        {checkButtonLabel}
      </Button>
    );
    return () => setBottomBarContent?.(null);
  }, [
    currentIndex,
    isLastActivity,
    isCurrentAnswered,
    isSubmitting,
    isCompleting,
    isContentHidden,
    feedback,
    hideCheckButton,
    showMistakesReviewPrompt,
    setBottomBarContent,
    checkButtonLabel,
  ]);

  const mistakesInListCount = failedActivityIds.filter((id) =>
    activityList.some((a) => a.id === id)
  ).length;

  if (isContentHidden) {
    return <div className={styles.story__activities} />;
  }

  /**
   * Entrada de cada actividad: pequeño fade + slide-up. Combinado con el
   * fade-in del wrapper exterior (en `StoryDetailPage`) se siente como
   * una cascada — primero la pantalla y, justo después, los elementos de
   * la actividad. Para las actividades siguientes (avanzar/retroceder)
   * Framer reusa el mismo `key=currentActivity.id` y la animación se
   * repite cada vez que cambia el id, suavizando el cambio entre preguntas.
   */
  const ENTRANCE_EASE = [0.4, 0, 0.2, 1] as const;

  return (
    <div className={styles.story__activities}>
      {currentActivity && (
        <motion.div
          key={currentActivity.id}
          className={`${styles.story__activityCard} ${
            showValidation && !isCurrentAnswered
              ? styles['story__activityCard--error']
              : ''
          }`}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.36, ease: ENTRANCE_EASE, delay: 0.08 }}
        >
          {/* Question text — hidden for vocabulary_card (it has its own header) */}
          {currentActivity.type !== 'vocabulary_card' && (
            <p className={styles.story__activityQuestion}>{currentActivity.question}</p>
          )}

          {/* ── Multiple Choice ─────────────────────────────────────────── */}
          {currentActivity.type === 'multiple_choice' && (
            <div className={styles.story__activityOptions} role="group">
              {currentActivity.options.map((option, idx) => (
                <Button
                  key={idx}
                  variant="secondary"
                  fullWidth
                  className={styles.story__optionBtn}
                  disabled={!!feedback}
                  onClick={() => handleMultipleChoiceChange(currentActivity.id, idx.toString())}
                >
                  {option}
                </Button>
              ))}
            </div>
          )}

          {/* ── True / False ─────────────────────────────────────────────── */}
          {currentActivity.type === 'true_false' && (
            <div className={styles.story__activityOptions} role="group">
              {[
                { value: 'true', label: t('activities.true') },
                { value: 'false', label: t('activities.false') },
              ].map((opt) => (
                <Button
                  key={opt.value}
                  variant="secondary"
                  fullWidth
                  className={styles.story__optionBtn}
                  disabled={!!feedback}
                  onClick={() => handleTrueFalseChange(currentActivity.id, opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          )}

          {/* ── Fill in Blank Tap (múltiples blancos) ─────────────────────── */}
          {currentActivity.type === 'fill_in_blank_tap' && (() => {
            const act = currentActivity;
            const ans = answers[currentActivity.id] as FillInBlankTapAnswer | undefined;
            const correctness = feedback?.correctness;
            const inRetry = !!retryUsed[currentActivity.id] && !feedback;
            const lockedPositions =
              inRetry && ans?.answers
                ? ans.answers
                    .map((w, i) => (w ? i : -1))
                    .filter((i) => i >= 0)
                : undefined;
            const retryKey = tapRetryKey[currentActivity.id] ?? 0;
            return (
              <FillInBlankTap
                key={`${currentActivity.id}-${retryKey}`}
                activity={act}
                answer={ans}
                onChange={(answersArr) => {
                  setShowValidation(false);
                  setAnswers((prev) => ({
                    ...prev,
                    [currentActivity.id]: {
                      activityId: currentActivity.id,
                      type: 'fill_in_blank_tap',
                      answers: answersArr,
                      timeSpent: 0,
                    } as FillInBlankTapAnswer,
                  }));
                }}
                onAllFilled={() => handleCheckRef.current?.()}
                correctness={correctness}
                lockedPositions={lockedPositions}
                disabled={!!feedback}
              />
            );
          })()}

          {/* ── Matching ─────────────────────────────────────────────────── */}
          {currentActivity.type === 'matching' && (
            <MatchingCards
              activity={currentActivity}
              answer={answers[currentActivity.id] as MatchingAnswer | undefined}
              onChange={(left, right) => handleMatchingChange(currentActivity.id, left, right)}
              disabled={!!feedback}
              feedback={feedback ?? undefined}
            />
          )}

          {/* ── Ordering (tap-based) ─────────────────────────────────────── */}
          {currentActivity.type === 'ordering' && (() => {
            const act = currentActivity;
            const ans = answers[currentActivity.id] as OrderingAnswer | undefined;
            const correctness = feedback?.correctness;
            const retryKey = tapRetryKey[currentActivity.id] ?? 0;
            return (
              <OrderingTap
                key={`${currentActivity.id}-${retryKey}`}
                activity={act}
                answer={ans}
                onChange={(order) => handleOrderingChange(currentActivity.id, order)}
                onAllPlaced={() => handleCheckRef.current?.()}
                correctness={correctness}
                disabled={!!feedback}
              />
            );
          })()}

          {/* ── Vocabulary Card (informational) ─────────────────────────── */}
          {currentActivity.type === 'vocabulary_card' && (() => {
            const act = currentActivity;

            const resolvedWordAudioUrl =
              act.wordAudioUrl && act.wordAudioUrl.length > 0 ? act.wordAudioUrl : undefined;

            const handleWordPlay = (e: React.MouseEvent) => {
              e.stopPropagation();
              if (!resolvedWordAudioUrl) return;
              if (!wordAudioRef.current) {
                wordAudioRef.current = new Audio(resolvedWordAudioUrl);
                wordAudioRef.current.onended = () => setWordAudioPlaying(false);
              }
              if (wordAudioPlaying) {
                wordAudioRef.current.pause();
                wordAudioRef.current.currentTime = 0;
                setWordAudioPlaying(false);
              } else {
                void wordAudioRef.current.play();
                setWordAudioPlaying(true);
              }
            };

            const PlayButton = resolvedWordAudioUrl ? (
              <button
                type="button"
                className={styles.story__vocabCardPlayBtn}
                onClick={handleWordPlay}
                aria-label={
                  wordAudioPlaying ? t('activities.pausePronunciation') : t('activities.playPronunciation')
                }
              >
                {wordAudioPlaying ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>
            ) : null;

            const isSaved = isVocabSaved(act.word);

            const handleSaveToggle = (e: React.MouseEvent) => {
              e.stopPropagation();
              if (isSaved) {
                void removeVocabItem(act.word);
              } else {
                void saveVocabItem({
                  word: act.word,
                  partOfSpeech: act.partOfSpeech,
                  definition: act.definition,
                  example: act.example,
                  wordAudioUrl: resolvedWordAudioUrl,
                });
              }
            };

            return (
              <div className={styles.story__vocabCard}>
                <IconButton
                  icon={Bookmark}
                  variant="bare"
                  size="md"
                  active={isSaved}
                  className={styles.story__vocabCardSaveBtn}
                  onClick={handleSaveToggle}
                  aria-pressed={isSaved}
                  aria-label={
                    isSaved
                      ? t('activities.removeVocabAria', { word: act.word })
                      : t('activities.saveVocabAria', { word: act.word })
                  }
                />
                {resolvedWordAudioUrl && (
                  <div className={styles.story__vocabCardPlayWrap}>
                    {PlayButton}
                  </div>
                )}
                <div className={styles.story__vocabCardWordRow}>
                  <span className={styles.story__vocabCardWord}>{act.word}</span>
                  <span className={styles.story__vocabCardPos}>{act.partOfSpeech}</span>
                </div>
                <p className={styles.story__vocabCardDefinition}>{act.definition}</p>
                <p className={styles.story__vocabCardExample}>"{act.example}"</p>
              </div>
            );
          })()}

          {/* ── Audio: Escuchar y seleccionar traducción ─────────────────── */}
          {currentActivity.type === 'audio_translation_select' && (() => {
            const act = currentActivity;
            return (
              <div className={styles.story__audioActivity}>
                <AudioActivityPlayer
                  audioUrl={act.audioUrl}
                  audioText={act.audioText}
                  className={styles.story__audioActivityPlayer}
                />
                <div className={styles.story__activityOptions} role="group">
                  {act.options.map((option, idx) => (
                    <Button
                      key={idx}
                      variant="secondary"
                      fullWidth
                      className={styles.story__optionBtn}
                      disabled={!!feedback}
                      onClick={() => handleAudioTranslationSelectChange(currentActivity.id, idx.toString())}
                    >
                      {option}
                    </Button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ── Audio: Escuchar y ordenar frase ─────────────────────────── */}
          {currentActivity.type === 'audio_ordering' && (() => {
            const act = currentActivity;
            const ans = answers[currentActivity.id] as AudioOrderingAnswer | undefined;
            // Build a synthetic OrderingActivity from audio_ordering fields
            const syntheticOrdering: OrderingActivity = {
              id: act.id,
              type: 'ordering',
              question: act.question,
              points: act.points,
              order: act.order,
              explanation: act.explanation,
              explanation_translation: act.explanation_translation,
              items: act.words,
              correctOrder: act.correctOrder,
            };
            const syntheticAnswer: OrderingAnswer | undefined = ans
              ? { activityId: ans.activityId, type: 'ordering', order: ans.order, timeSpent: ans.timeSpent }
              : undefined;
            const correctness = feedback?.correctness;
            const retryKey = tapRetryKey[currentActivity.id] ?? 0;
            return (
              <div className={styles.story__audioActivity}>
                <AudioActivityPlayer
                  audioUrl={act.audioUrl}
                  audioText={act.audioText}
                  className={styles.story__audioActivityPlayer}
                />
                <OrderingTap
                  key={`${currentActivity.id}-${retryKey}`}
                  activity={syntheticOrdering}
                  answer={syntheticAnswer}
                  onChange={(order) => handleAudioOrderingChange(currentActivity.id, order)}
                  onAllPlaced={() => handleCheckRef.current?.()}
                  correctness={correctness}
                  disabled={!!feedback}
                />
              </div>
            );
          })()}

          {/* ── Audio: Escuchar y escribir palabra ──────────────────────── */}
          {currentActivity.type === 'audio_dictation' && (() => {
            const act = currentActivity;
            const ans = answers[currentActivity.id] as AudioDictationAnswer | undefined;
            return (
              <div className={styles.story__audioActivity}>
                <AudioActivityPlayer
                  audioUrl={act.audioUrl}
                  audioText={act.audioText}
                  className={styles.story__audioActivityPlayer}
                />
                <input
                  type="text"
                  className={styles.story__audioDictationInput}
                  placeholder={t('activities.audioDictationPlaceholder', { defaultValue: 'Escribe la palabra…' })}
                  value={ans?.answer ?? ''}
                  disabled={!!feedback}
                  onChange={(e) => handleAudioDictationChange(currentActivity.id, e.target.value)}
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-label={t('activities.audioDictationAriaLabel', { defaultValue: 'Escribe la palabra que escuchas' })}
                />
              </div>
            );
          })()}

          {/* ── Traducir frase con tap ───────────────────────────────────── */}
          {currentActivity.type === 'translation_tap' && (() => {
            const act = currentActivity;
            const ans = answers[currentActivity.id] as TranslationTapAnswer | undefined;
            // Build a synthetic FillInBlankTap: N blanks (one per correct word), words pool
            const blanks = act.correctAnswers.map(() => '_____').join(' ');
            const syntheticActivity: FillInBlankTapActivity = {
              id: act.id,
              type: 'fill_in_blank_tap',
              question: act.question,
              points: act.points,
              order: act.order,
              explanation: act.explanation,
              explanation_translation: act.explanation_translation,
              textWithBlanks: blanks,
              correctAnswers: act.correctAnswers,
              words: act.words,
            };
            const syntheticAnswer: FillInBlankTapAnswer | undefined = ans
              ? { activityId: ans.activityId, type: 'fill_in_blank_tap', answers: ans.answers, timeSpent: ans.timeSpent }
              : undefined;
            const correctness = feedback?.correctness;
            const inRetry = !!retryUsed[currentActivity.id] && !feedback;
            const lockedPositions =
              inRetry && ans?.answers
                ? ans.answers
                    .map((w, i) => (w ? i : -1))
                    .filter((i) => i >= 0)
                : undefined;
            const retryKey = tapRetryKey[currentActivity.id] ?? 0;
            return (
              <div className={styles.story__translationTap}>
                <p className={styles.story__translationSource}>{act.sourceText}</p>
                <FillInBlankTap
                  key={`${currentActivity.id}-${retryKey}`}
                  activity={syntheticActivity}
                  answer={syntheticAnswer}
                  onChange={(answersArr) => handleTranslationTapChange(currentActivity.id, answersArr)}
                  onAllFilled={() => {
                    const latest = answersRef.current[currentActivity.id] as TranslationTapAnswer | undefined;
                    if (latest?.answers.length === act.correctAnswers.length) {
                      handleCheckRef.current?.();
                    }
                  }}
                  correctness={correctness}
                  lockedPositions={lockedPositions}
                  disabled={!!feedback}
                />
              </div>
            );
          })()}

          {/* ── Selección de traducción correcta ────────────────────────── */}
          {currentActivity.type === 'translation_select' && (() => {
            const act = currentActivity;
            return (
              <div className={styles.story__translationSelect}>
                <p className={styles.story__translationSource}>{act.sourceText}</p>
                <div className={styles.story__activityOptions} role="group">
                  {act.options.map((option, idx) => (
                    <Button
                      key={idx}
                      variant="secondary"
                      fullWidth
                      className={styles.story__optionBtn}
                      disabled={!!feedback}
                      onClick={() => handleTranslationSelectChange(currentActivity.id, idx.toString())}
                    >
                      {option}
                    </Button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* ── Completar frase con tap (un blank) ──────────────────────── */}
          {currentActivity.type === 'sentence_complete_tap' && (() => {
            const act = currentActivity;
            const ans = answers[currentActivity.id] as SentenceCompleteTapAnswer | undefined;
            // Build a synthetic FillInBlankTap with a single blank
            const syntheticActivity: FillInBlankTapActivity = {
              id: act.id,
              type: 'fill_in_blank_tap',
              question: act.question,
              points: act.points,
              order: act.order,
              explanation: act.explanation,
              explanation_translation: act.explanation_translation,
              textWithBlanks: act.textWithBlanks,
              correctAnswers: [act.correctAnswer],
              words: act.words,
            };
            const syntheticAnswer: FillInBlankTapAnswer | undefined = ans?.answer
              ? { activityId: ans.activityId, type: 'fill_in_blank_tap', answers: [ans.answer], timeSpent: ans.timeSpent }
              : undefined;
            const correctness =
              feedback && ans?.answer !== undefined
                ? [ans.answer.trim().toLowerCase() === act.correctAnswer.trim().toLowerCase()]
                : undefined;
            return (
              <FillInBlankTap
                key={currentActivity.id}
                activity={syntheticActivity}
                answer={syntheticAnswer}
                onChange={(answersArr) => {
                  handleSentenceCompleteTapChange(currentActivity.id, answersArr[0] ?? '');
                }}
                onAllFilled={() => handleCheckRef.current?.()}
                correctness={correctness}
                disabled={!!feedback}
              />
            );
          })()}

          {/* Validation message */}
          {showValidation && !isCurrentAnswered && (
            <p className={styles.story__activityError}>
              <Icon icon={XCircle} size="sm" />
              {t('activities.pleaseAnswer')}
            </p>
          )}
        </motion.div>
      )}

      {/* Feedback panel */}
      {feedback && (
        <div
          className={`${styles.story__feedback} ${
            feedback.isCorrect
              ? styles['story__feedback--correct']
              : styles['story__feedback--incorrect']
          }`}
        >
          <div className={styles.story__feedbackBody}>
            <div className={styles.story__feedbackHeader}>
              {feedback.isCorrect ? (
                <div className={styles.story__feedbackIconCircle}>
                  <Icon icon={Check} size="sm" className={styles.story__feedbackIconInverse} />
                </div>
              ) : (
                <div className={`${styles.story__feedbackIconCircle} ${styles['story__feedbackIconCircle--incorrect']}`}>
                  <Icon icon={X} size="sm" className={styles.story__feedbackIconInverse} />
                </div>
              )}
              <span
                className={`${styles.story__feedbackTitle} ${
                  feedback.isCorrect
                    ? styles['story__feedbackTitle--correct']
                    : styles['story__feedbackTitle--incorrect']
                }`}
              >
                {feedback.isCorrect
                  ? t('activities.correct')
                  : feedback.allowRetry
                    ? t('activities.almostCorrect')
                    : t('activities.incorrect')}
              </span>
            </div>

            {!feedback.isCorrect && feedback.explanation && (
              <button
                type="button"
                className={styles.story__feedbackWhyWrong}
                onClick={() => setWhyWrongOpen(true)}
              >
                {t('activities.whyWrong')}
              </button>
            )}
          </div>

          {feedback.allowRetry ? (
            <div className={styles.story__feedbackButtons}>
              <Button
                variant="error"
                size="lg"
                onClick={handleRetry}
                fullWidth
              >
                {t('activities.retry')}
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={handleContinue}
                fullWidth
              >
                {t('activities.continueFeedback')}
              </Button>
            </div>
          ) : (
            <Button
              variant={feedback.isCorrect ? 'success' : 'error'}
              size="lg"
              onClick={handleContinue}
              fullWidth
            >
              {t('activities.continueFeedback')}
            </Button>
          )}
        </div>
      )}

      <MistakesReviewDrawer
        open={showMistakesReviewPrompt}
        mistakesCount={mistakesInListCount}
        onReviewMistakes={handleReviewMistakesFromPrompt}
        onSubmitAnyway={handleSubmitAnywayFromPrompt}
        disableSubmit={isSubmitting || isCompleting}
      />

      <WrongExplanationDrawer
        open={whyWrongOpen}
        onOpenChange={setWhyWrongOpen}
        explanation={feedback?.explanation ?? null}
        title={t('activities.whyWrongDrawerTitle')}
        closeLabel={t('activities.closeWhyWrong')}
      />

      {/* Confirmación de salida sin terminar (misma modal que Ajustes/Perfil) */}
      <Dialog open={leaveOpen} onOpenChange={(open) => !open && handleLeaveStay()}>
        <DialogContent hideDefaultClose>
          <IconButton
            icon={X}
            aria-label={t('activities.leaveGuard.close')}
            onClick={handleLeaveStay}
            className={styles.story__modalClose}
          />
          <DialogHeader>
            <DialogTitle>{t('activities.leaveGuard.title')}</DialogTitle>
            <DialogDescription>{t('activities.leaveGuard.message')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={handleLeaveConfirm}>
              {t('activities.leaveGuard.leave')}
            </Button>
            <Button variant="primary" onClick={handleLeaveStay}>
              {t('activities.leaveGuard.stay')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
